import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { Room } from 'livekit-client';
import { useTextConversation, type SendMessageOptions } from './useTextConversation';
import { computeVoiceStartReady } from './voiceStartReady';
import {
  useVoiceConversation,
  type VoiceState,
  type VoicePauseReason,
} from './useVoiceConversation';
import type { MicErrorKind } from './micError';
import type { VoiceEndReason } from '@shared/livekit-constants';
import { mapServerTurnsToV2 } from '@/lib/conversationBootIntent';
import type {
  ContentBlock,
  V2ConversationTurn,
  WidgetBlock,
} from '@shared/conversation-schema';
import type { VoiceTranscriptData } from '@shared/voice-data-channel';
import type { SnapshotAction } from '@shared/v2-schemas';
import { applyWidgetReplace, dedupeCardsByWidgetId, assembleFinalStateTurns } from './widgetReplace';

export type InputMode = 'text' | 'voice';

/**
 * Lifecycle phase for the conversation. Slice 12.
 *
 * - `mounting`: projectId not yet resolved, OR startSession() has not been attempted yet
 *   (covers both pre-resolution AND idle pre-trigger states).
 * - `connecting`: startSession in flight; conversationId not yet returned.
 * - `awaiting_first_turn`: conversationId resolved; awaiting Anaka's first content block.
 * - `ready`: at least one content block has arrived (text or widget). Input is gated open.
 * - `error`: terminal-ish; consumers should surface the `error` field.
 */
export type ConversationPhase =
  | 'mounting'
  | 'connecting'
  | 'awaiting_first_turn'
  | 'ready'
  | 'error';

export interface ConversationOptions {
  projectId: string;
  mode: string;
  defaultInputMode?: InputMode;
  activityId?: string;
  participantId?: string;
  /**
   * Client-owned conversation id from `useConversationLifecycle` (α4). When null,
   * `startSession` omits reconnect fields. When set, forwarded as reconnect per
   * API (`is_reconnect` + `previous_conversation_id`).
   *
   * NOTE: this no longer contributes to the `surfaceKey` that drives transcript
   * resets. Use `lifecycleSurfaceResetSignal` for that purpose so that the first
   * mint of a lifecycle id (null → UUID on first message sent) does not
   * spuriously clear the in-progress home transcript.
   */
  conversationId?: string | null;
  /**
   * Monotonically-increasing counter supplied by `ConversationContext`. It
   * increments only on explicit lifecycle rotations (UUID-A → UUID-B), i.e.
   * when the user explicitly resets the conversation. It does NOT increment on
   * first mint (null → UUID), so the home transcript is never wiped on the
   * user's first send.
   *
   * The value is included in `surfaceKey` instead of the raw `conversationId` to
   * drive transcript resets and SSE aborts. Defaults to `0` when omitted so
   * existing call-sites in tests that pass `conversationId` directly remain valid.
   */
  lifecycleSurfaceResetSignal?: number;
  /**
   * Route-derived stability hint (typically the project shortId from the URL,
   * or `'home'` from the provider). Combined with `projectId` and
   * `lifecycleSurfaceResetSignal` to build an internal `surfaceKey` that drives
   * transcript resets and SSE aborts in the composed text transport.
   */
  conversationSurfaceShortId?: string | null;
  /**
   * Wouter pathname (or equivalent) sent as `current_page` on start/message.
   */
  currentPage?: string;
  onContentBlock?: (block: ContentBlock) => void;
  onWidgetData?: (data: { event: string; widget_id?: string; data: Record<string, unknown> }) => void;
  /** T020 — SSE `navigate`; parent routes client + enqueues continuation handoff. */
  onNavigate?: (event: {
    target: string;
    project_short_id?: string;
    tab?: string;
    conversation_id?: string;
  }) => void;
  /** α7 — SSE `cloud_idle_ended`; parent dispatches lifecycle. */
  onCloudIdleEnded?: (data: { conversation_id: string }) => void;
  /** 011 F3 — SSE `done` with end_session + end_reason:'guardrail'; parent dispatches lifecycle. */
  onCloudGuardrailEnded?: (data: { conversation_id: string }) => void;
  /**
   * ANA-575 — the voice agent intentionally ended the session (goodbye /
   * scope-lock / cap / guardrail). The parent dispatches the matching lifecycle
   * end (`cloud_session_ended` for complete/scope_locked/cap; `cloud_guardrail_ended`
   * for guardrail) so the surface closes gracefully. NOT called for an `error`
   * end (that keeps the retryable voice-error path).
   */
  onVoiceSessionEnded?: (reason: 'complete' | 'scope_locked' | 'cap' | 'guardrail') => void;
  /** 038 — SSE `done` with reconnect:'cut_off'; a ≤2-min scoping reconnect ("we got cut off") ack. */
  onReconnectNotice?: (data: { conversation_id: string; reconnect: 'cut_off' }) => void;
  /**
   * 015 — FR-VOICE-003: invoked when the user ends the voice session (the End
   * control → `disconnectVoice`). The parent resets the lifecycle so the NEXT
   * Start begins a fresh conversation (no reconnect). Pause does NOT call this.
   */
  onEndConversation?: () => void;
  /**
   * Called when a silent transcript hydrate finds the conversation id has **no
   * server row** (404 — expired, self-heal-swept, or rolled back). Lets the owner
   * clear the stale client id (e.g. `lifecycle.reset()`) so the next boot starts a
   * fresh conversation instead of re-hydrating/reconnecting a dead one (P1.3).
   */
  onOrphanedConversationId?: () => void;
  onError?: (error: Error) => void;
  /**
   * When true, the hook calls `startSession()` automatically on mount once
   * `projectId` is non-empty. Defaults to `false` for backwards compatibility
   * with surfaces that drive start manually.
   */
  proactiveStart?: boolean;
  /**
   * 020 T014 — whether this surface renders a full conversation transcript
   * (overview / detailed shell). When true and there is a prior `conversationId`
   * (and `continuationRequested` is false), the hook silently fetches and
   * hydrates the transcript via `GET /api/v1/conversation/:id/turns` without
   * firing `startSession`. Ignored when `proactiveStart` is true (warm_continue
   * takes precedence).
   */
  isDetailedConversationSurface?: boolean;
  /**
   * Scoping resume — the conversation_id of an existing scoping session for this
   * project, sourced from briefing-context. Used as a fallback reconnect target
   * when `lifecycleConversationId` (sessionStorage) is null (e.g. cross-session
   * resume after TTL expiry). Takes effect in `startSession` and transcript
   * hydration.
   *
   * `null` is an explicit override: briefing-context loaded and found no resumable
   * session — do NOT fall back to a stale `lifecycleConversationId` from sessionStorage.
   * `undefined` = not applicable for this surface; use sessionStorage as normal.
   */
  resumeConversationId?: string | null;
  /**
   * When true, `resumeConversationId` points at a resumable session whose TTL has
   * lapsed (expired scoping). Its transcript is still durable, but it must NOT be
   * auto-hydrated on mount — the user re-enters explicitly via the orient card, so
   * hydration is deferred until `startSession` has been attempted (the voice/text
   * click). Healthy (non-expired) resumes leave this false and hydrate immediately.
   */
  resumeIsDeferred?: boolean;
}

export interface ConversationReturn {
  inputMode: InputMode;
  /**
   * Set the input transport. `opts.autoStartText` (default `true`): when switching
   * to text after a start was attempted but produced no content (a failed/stalled
   * first turn), kick a fresh text start so the composer unblocks (ANA-282 — the
   * in-bar text toggle relies on this). Callers that own the start themselves
   * (ProjectScopingPage.beginCall calls setInputMode THEN startSession) pass `false`
   * so this does not ALSO start, which would double-start on the poison-pill resume
   * path (ANA-381). No-op for `mode === 'voice'` (the auto-start branch is text-only).
   */
  setInputMode: (mode: InputMode, opts?: { autoStartText?: boolean }) => void;
  conversationId: string | null;
  isThinking: boolean;
  /**
   * Text turn in flight with content arriving — the "responding" phase that
   * follows `isThinking`. Optional so existing `ConversationReturn` mocks need
   * no update; defaults to falsy when absent.
   */
  isResponding?: boolean;
  voiceState: VoiceState;
  isConnected: boolean;
  isMuted: boolean;
  /** Voice pause pass-through from `useVoiceConversation`. */
  paused: boolean;
  /**
   * Why the call is paused (null when not paused) — drives the paused status copy.
   * Optional on this aggregate view (display-only, undefined-safe at every consumer);
   * always present on the underlying `VoiceConversationReturn`.
   */
  pauseReason?: VoicePauseReason | null;
  /**
   * Accumulated paused wall-time (ms) since the last `consumeElapsedPause`.
   * Pure pass-through of `voiceHook.elapsedPauseMs` under a UI-friendlier name
   * so `<ConversationInputBar pausedMs={…}>` can read it without poking at the
   * underlying voice hook.
   */
  pausedMs: number;
  /**
   * Wall-clock timestamp (ms since epoch) recorded the moment the most recent
   * `connectVoice()` resolves. Cleared back to `null` on `disconnectVoice()`.
   * `<VoiceSessionTimer>` uses this to render elapsed time without owning a
   * `Date.now()` ref of its own.
   */
  voiceStartedAt: number | null;
  error: Error | null;
  /** LiveKit room provided to the live chrome via `RoomContext`. Read after `voiceState` updates. */
  room: Room | null;
  livekitUrl: string | null;
  /**
   * True when the microphone could not be published on connect (denied prompt).
   * The session still connects; consumers surface a soft "mic blocked" hint.
   */
  micBlocked: boolean;
  /**
   * Why the mic publish failed (null when fine) — drives honest, actionable
   * copy. Optional (like `statusLabelOverride`) so existing full-object mocks
   * that predate it stay valid; the real hook always provides it.
   */
  micErrorKind?: MicErrorKind | null;
  /**
   * ANA-728/ANA-738 (B1) — true while the mic is AUTO-locked during `thinking`
   * (Anaka is working). Drives the honest "mic paused — tap to interrupt" cue,
   * distinct from a user's own mute. Optional so full-object mocks predating it
   * stay valid; the real hook always provides it.
   */
  micLocked?: boolean;
  /** Custom mid-turn voice status label from n8n (spec 015 US4); null/undefined otherwise. */
  statusLabelOverride?: string | null;
  /**
   * ANA-575 — why the voice agent ended the session, set once `voiceState` is
   * `'ended'` (null otherwise). Drives reason-aware close copy in the voice bar.
   * Optional (like `statusLabelOverride`) so predating full-object mocks stay valid.
   */
  voiceEndedReason?: VoiceEndReason | null;
  /**
   * Text-mode `send_status_update` mid-turn bridge line — null when no status
   * update is currently active. Cleared as soon as real content arrives for the
   * turn (superseded, per `TextConversationOptions.onStatusUpdate`'s own
   * contract) or when the user starts a new turn. Voice doesn't need this: the
   * agent speaks the same `text` aloud directly (see `livekit-room-publish.ts`).
   * Optional (like `statusLabelOverride`/`micErrorKind`) so existing
   * full-object mocks that predate it stay valid.
   */
  textStatusUpdate?: { text: string; label: string } | null;
  /**
   * Lifecycle phase derived from (projectId, startSessionAttempted,
   * conversationId, first content block, error). Consumers (e.g.
   * `<ChatThread>`) gate their initial affordance on this — the action
   * carousel stays hidden until `'ready'`.
   */
  phase: ConversationPhase;
  /** Sugar for `phase === 'ready'` — first assistant content (text or widget) received. */
  isReady: boolean;
  /**
   * n8n-send readiness: `true` when the server has minted a `conversation_id` and
   * there is no current transport error, i.e. `phase` is `awaiting_first_turn` or
   * `ready`. This is the gate for letting the user type and send to n8n; it is
   * distinct from {@link isReady}, which only becomes true after the first
   * assistant content block. **Implication:** `isReady` always implies
   * `inputReady`, but not vice versa. Home `<HomeLobby>` and overview
   * `<ChatThread>` should pass this into `<ConversationInputBar inputReady>`; post-first-turn
   * affordances like `<ChatActionsCarousel>` should keep using `isReady`.
   */
  inputReady: boolean;
  /**
   * 004-p7 A3.2 (ANA-658) — safe to START a voice call now? False on a surface that
   * auto-starts text (scoping/proactiveStart) until the server resolves the
   * conversation id; the voice-start control is disabled while false so voice can't
   * key a LiveKit room on a fallback id that then diverges from the text session.
   * Always true on a pure-lazy surface (home). Wire to
   * `<ConversationInputBar voiceStartDisabled={!voiceStartReady}>`.
   */
  voiceStartReady: boolean;
  /**
   * Accumulated transcript turns, grouped by canonical `turn_id`. Populated by
   * the hook itself from text/voice content blocks (assistant) and from the
   * wrapped `sendMessage` (user). `<ChatThread>` reads this directly to feed
   * `<V2ConversationTranscript turns={…} />` — there is no separate listener
   * API; turn ownership lives here so all consumers see the same history.
   */
  turns: V2ConversationTurn[];
  /**
   * ANA-371 — true while the silent transcript hydrate (`GET /:id/turns`) is in
   * flight on a resumable detailed surface. Consumers render a message skeleton
   * while this is true and `turns` is still empty, instead of a blank thread.
   * Optional (like {@link isResponding}) so existing ConversationReturn mocks need
   * no update; the live hook always provides it.
   */
  isHydratingTranscript?: boolean;
  /**
   * Spec 014 US1 — cold-entry entry pills. On a cold direct entry to update
   * mode the server emits `turn_actions` WITHOUT any assistant text turn
   * (Anaka stays silent until the user acts). Those actions cannot attach to a
   * last assistant turn because none exists, so they live here instead. The
   * standalone outlined `<ChatActionsCarousel>` in `<ChatThread>` renders them
   * when the transcript has no assistant turn yet. Cleared the moment the user
   * sends a message or Anaka's first content block arrives, so they behave as
   * one-shot entry pills rather than persistent affordances.
   */
  entryActions: SnapshotAction[];
  startSession: (opts?: { deferOpening?: boolean }) => Promise<void>;
  sendMessage: (text: string, options?: Omit<SendMessageOptions, 'elapsedPauseMs'>) => Promise<void>;
  /**
   * Re-issue whichever conversation request last failed (text `startSession`
   * or `sendMessage`). Lets the UI offer a single Retry affordance whose
   * behaviour matches the operation that errored.
   */
  retry: () => Promise<void>;
  connectVoice: () => Promise<void>;
  disconnectVoice: () => void;
  /** Pass-through to `useVoiceConversation.pause` for `<ConversationInputBar>` wiring. */
  pauseVoice: () => void;
  /** Pass-through to `useVoiceConversation.resume`. */
  resumeVoice: () => Promise<void>;
  toggleMute: () => void;
  /**
   * ANA-414 — true while the idle "still there?" phase-1 prompt is showing
   * (v1.0.4 two-phase idle). Forwards `useVoiceConversation.idlePrompting` so
   * UI components can render the "Still there? I'll pause shortly…" message
   * without coupling to the underlying voice hook. Only meaningful when
   * `inputMode === 'voice'`; always `false` in text mode.
   */
  idlePrompting?: boolean;
  /**
   * ANA-409 — true once the call is within its pre-cap warning window of the
   * hard cost-cap auto-pause. Forwards `useVoiceConversation.sessionCapWarning`
   * so the surface can offer to continue or wrap up before the room is
   * yanked. Only meaningful when `inputMode === 'voice'`.
   */
  sessionCapWarning?: boolean;
  /** Pass-through to `useVoiceConversation.extendSession` — resets the cost-cap window. */
  extendSession?: () => void;
  /**
   * ANA-542 — pass-through to `useVoiceConversation.lockScopeRequested`. Shared
   * (not per-component) guard so a "Lock the scope" tap can't be duplicated by
   * switching shell mode (peek/detailed's `WiredConversationInputBar` vs
   * focused's `ChatThread`) between the tap and its response landing.
   */
  lockScopeRequested?: boolean;
  /** Pass-through to `useVoiceConversation.requestLockScope`. */
  requestLockScope?: () => void;
}

/**
 * 015 (I-1): mint the client lazy/fallback conversation id as a UUID so the
 * LiveKit room name shares ONE format with server-minted ids — no more
 * `anaka-conv_<ts>` vs `anaka-<uuid>` variance across surfaces. The id is still
 * shared with the text hook (`lazyConversationId`) and honoured by the server
 * (`/message` lazy-create + `anaka-${id}` room derivation), so convergence
 * (PRs #556/#558) is preserved — only the format changes. Guarded for legacy
 * envs without `crypto.randomUUID`, matching useTextConversation's lazy mint.
 */
function mintConversationId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Bug B (ANA-614) — client kill-switch for the voice-resume greeting-cycle fix.
 * ON (default): a voice connect always shows the greeting/recap cycle
 * ('preparing' → thinking → speaking), and on a RESUME (turns already present) a
 * slow/absent recap degrades to 'listening' instead of the cold-start error — so
 * a resumed call no longer sits mislabelled on 'listening' while Anaka recaps.
 * OFF (`VITE_VOICE_RESUME_GREETING_CYCLE=false`): restores the exact prior
 * behaviour (greeting shown only on a fresh start, turns===0).
 */
const VOICE_RESUME_GREETING_CYCLE_ENABLED =
  (import.meta.env.VITE_VOICE_RESUME_GREETING_CYCLE ?? 'true') !== 'false';

export function useConversation(options: ConversationOptions): ConversationReturn {
  const {
    projectId,
    mode,
    defaultInputMode,
    activityId,
    participantId,
    conversationId: lifecycleConversationId = null,
    lifecycleSurfaceResetSignal = 0,
    conversationSurfaceShortId = null,
    currentPage,
    onContentBlock,
    onWidgetData,
    onCloudIdleEnded,
    onCloudGuardrailEnded,
    onVoiceSessionEnded,
    onReconnectNotice,
    onEndConversation,
    onOrphanedConversationId,
    onNavigate,
    onError,
    proactiveStart = false,
    isDetailedConversationSurface = false,
    resumeConversationId,
    resumeIsDeferred = false,
  } = options;

  const [inputMode, setInputModeState] = useState<InputMode>(defaultInputMode ?? 'text');

  // ANA-371 — true while the silent transcript hydrate (`GET /:id/turns`) is in
  // flight on a resumable detailed surface, so the surface can render a message
  // skeleton instead of a blank thread until restored history lands.
  const [isHydratingTranscript, setIsHydratingTranscript] = useState(false);

  const [fallbackConversationId, setFallbackConversationId] = useState(mintConversationId);
  // 032 Phase D (US2 / ANA-612 / MF7) — the server-resolved voice id, adopted when
  // `/voice/token` mints a fresh id for an owned-but-expired conversation. Highest
  // precedence in the id resolution below (it IS the id the room was born on), and
  // re-seated onto `sessionConversationIdRef` so a subsequent reconnect/hydrate
  // tracks the real room. Cleared on surface reset (a new session re-latches).
  const [adoptedVoiceConversationId, setAdoptedVoiceConversationId] = useState<string | null>(null);

  // Lifecycle bookkeeping that drives the `phase` derivation. We mirror
  // `firstContentBlockReceived` and `startSessionAttempted` in both ref + state
  // because the refs let our callbacks check the current value without
  // re-binding while the state slots force re-renders so `phase` recomputes.
  const firstContentBlockReceivedRef = useRef(false);
  const [firstContentBlockReceived, setFirstContentBlockReceived] = useState(false);
  const startSessionAttemptedRef = useRef(false);
  const [startSessionAttempted, setStartSessionAttempted] = useState(false);

  // Set when the user triggers voice while the conversation_id is still being
  // resolved server-side (see connectVoice). The deferred connect fires from an
  // effect once `text.conversationId` arrives.
  const pendingVoiceConnectRef = useRef(false);

  // FR-001a (spec 032) — the FROZEN session conversation identity. Latched once
  // (see the derivation below the text/voice hooks) and reused for the session's
  // life so a live LiveKit room is never re-keyed by a change in the derived
  // id precedence. Cleared on surface reset (the reset effect below).
  const sessionConversationIdRef = useRef<string | null>(null);

  // Monotonic counter for synthesizing unique widget_ids on voice events that
  // arrive within the same millisecond. We avoid `crypto.randomUUID` here so
  // tests don't have to polyfill it on legacy jsdom builds.
  const voiceWidgetCounterRef = useRef(0);

  // Slice 13: turn accumulation. We own `turns` here so every chat surface
  // reads from the same array. Coalescing relies purely on the trailing
  // turn's role: an assistant block appended after another assistant turn
  // extends it, while any user turn in between forces the next assistant
  // block to mint a fresh turn. Keeps the `setTurns` updater pure so React
  // 18 StrictMode's double-invoke doesn't drift counters.
  const [turns, setTurns] = useState<V2ConversationTurn[]>([]);
  // Spec 014 US1: cold-entry entry pills that arrive with no assistant turn to
  // attach to. Held separately from per-turn `actions` and rendered as a
  // standalone outlined carousel until the user acts. See `ConversationReturn.entryActions`.
  const [entryActions, setEntryActions] = useState<SnapshotAction[]>([]);
  // Text-mode `send_status_update` mid-turn bridge line (review P2 follow-up —
  // `useTextConversation`'s `onStatusUpdate` was defined but never forwarded).
  // Cleared on the next content block for the same turn (see
  // `handleTextContentBlock`) or when a new turn starts (see `appendUserTurn`).
  const [textStatusUpdate, setTextStatusUpdate] = useState<{ text: string; label: string } | null>(null);
  const turnCounterRef = useRef(0);
  // Live mirror of the transcript length, read at voice-connect time to decide
  // whether Anaka should greet: an empty transcript = fresh voice-first start
  // (greet); a populated one = the user is switching from text (don't greet).
  // A ref avoids re-creating the connect callbacks on every appended turn.
  const turnsLengthRef = useRef(0);
  turnsLengthRef.current = turns.length;
  // Live mirror of "does the transcript contain any assistant turn yet", read
  // synchronously when `turn_actions` arrive to decide whether they attach to a
  // turn (normal flow) or become standalone cold-entry pills (spec 014 US1).
  const hasAssistantTurnRef = useRef(false);
  hasAssistantTurnRef.current = turns.some((t) => t.role === 'assistant');

  // surfaceKey drives transcript resets and SSE aborts. It intentionally uses
  // `lifecycleSurfaceResetSignal` (a rotation-only counter) rather than the raw
  // `lifecycleConversationId` so that the first mint of a lifecycle id
  // (null → UUID on the user's first send) does not wipe the in-progress transcript.
  const surfaceKey = `${lifecycleSurfaceResetSignal}\0${projectId}\0${conversationSurfaceShortId ?? 'nil'}`;

  /**
   * On `surfaceKey` change (route hint, explicit lifecycle reset, or `projectId`):
   *
   * 1. Abort any in-flight SSE (via `useTextConversation` `transportSurfaceKey`).
   * 2. Clear transcript and session bookkeeping.
   * 3. **Do not** auto-call `startSession` here — the provider’s `proactiveStart`
   *    effect handles re-opening once `projectId` / identity stabilise. This split
   *    keeps the hook controlled; the caller owns *when* to reconnect.
   */
  useEffect(() => {
    turnCounterRef.current = 0;
    voiceWidgetCounterRef.current = 0;
    setTurns([]);
    setEntryActions([]);
    firstContentBlockReceivedRef.current = false;
    setFirstContentBlockReceived(false);
    startSessionAttemptedRef.current = false;
    setStartSessionAttempted(false);
    pendingVoiceConnectRef.current = false;
    // FR-001a — a new surface is a new session: unfreeze so the identity
    // re-latches for the new conversation (paired with the fallback re-mint).
    sessionConversationIdRef.current = null;
    setFallbackConversationId(mintConversationId());
    // 032 Phase D (US2) — a new surface drops any prior born-canonical adoption.
    setAdoptedVoiceConversationId(null);
  }, [surfaceKey]);

  const nextTurnId = useCallback((role: 'user' | 'assistant') => {
    turnCounterRef.current += 1;
    return `${role}_${Date.now()}_${turnCounterRef.current}`;
  }, []);

  const appendBlockToAssistantTurn = useCallback((block: ContentBlock) => {
    // Mint the candidate id outside the updater so the counter advances
    // exactly once per call even under StrictMode double-invocation. When
    // the trailing turn is already assistant we discard `newTurnId` and
    // extend the existing turn instead — the wasted id is acceptable.
    const newTurnId = nextTurnId('assistant');
    setTurns((prev) => {
      const last = prev[prev.length - 1];
      // T024 D5: never coalesce into a hydrated trailing turn (server-owned id).
      // Runtime-minted ids use `assistant_<ts>_<n>` / `user_<ts>_<n>` prefix;
      // hydrated/server ids don't — starting a fresh turn preserves transcript fidelity.
      const isRuntimeMinted =
        last?.role === 'assistant' &&
        /^assistant_\d+_\d+$/.test(last.turn_id);
      if (isRuntimeMinted) {
        const updated: V2ConversationTurn = {
          ...last,
          content: [...last.content, block],
        };
        return [...prev.slice(0, -1), updated];
      }
      return [
        ...prev,
        {
          turn_id: newTurnId,
          role: 'assistant',
          content: [block],
          timestamp: new Date().toISOString(),
        },
      ];
    });
  }, [nextTurnId]);

  // 004-p6 (T016) — a widget block routes through the id-keyed replace helper so
  // a materialized roster card carrying a stable, engagement-scoped `widget_id`
  // re-anchors the single live card at the frontier (leaving a `card_moved`
  // breadcrumb) instead of stacking a fresh copy each turn. Model-minted widgets
  // (unique ids) simply append — the helper's "id not found" path. Shared by both
  // the SSE (text) and voice paths so the behaviour is identical across modalities.
  const appendOrReplaceWidgetBlock = useCallback((block: WidgetBlock) => {
    const newTurnId = nextTurnId('assistant');
    setTurns((prev) =>
      applyWidgetReplace({ turns: prev, block, newTurnId, timestamp: new Date().toISOString() }),
    );
  }, [nextTurnId]);

  const appendUserTurn = useCallback((text: string) => {
    const turn_id = nextTurnId('user');
    const timestamp = new Date().toISOString();
    setTurns((prev) => [
      ...prev,
      {
        turn_id,
        role: 'user',
        content: [{ type: 'text', text }],
        timestamp,
      },
    ]);
    // A new turn starting makes any status line left over from the previous
    // turn's tail stale — clear it regardless of which transport the new turn uses.
    setTextStatusUpdate(null);
  }, [nextTurnId]);

  const handleTurnActions = useCallback((actions: SnapshotAction[]) => {
    // Spec 014 US1 — cold entry: when no assistant turn exists yet, the server
    // emitted entry pills with no accompanying text turn (Anaka stays silent
    // until the user acts). Attaching to "the last assistant turn" would drop
    // them, so stash them as standalone entry pills instead. Otherwise keep the
    // existing per-turn behaviour and attach to the last assistant turn.
    if (!hasAssistantTurnRef.current) {
      setEntryActions(actions);
      return;
    }
    setTurns((prev) => {
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].role !== 'assistant') continue;
        return [...prev.slice(0, i), { ...prev[i], actions }, ...prev.slice(i + 1)];
      }
      return prev;
    });
  }, []);

  const onContentBlockRef = useRef(onContentBlock);
  onContentBlockRef.current = onContentBlock;
  const onCloudIdleEndedRef = useRef(onCloudIdleEnded);
  onCloudIdleEndedRef.current = onCloudIdleEnded;
  const onCloudGuardrailEndedRef = useRef(onCloudGuardrailEnded);
  onCloudGuardrailEndedRef.current = onCloudGuardrailEnded;
  const onVoiceSessionEndedRef = useRef(onVoiceSessionEnded);
  onVoiceSessionEndedRef.current = onVoiceSessionEnded;
  const onReconnectNoticeRef = useRef(onReconnectNotice);
  onReconnectNoticeRef.current = onReconnectNotice;
  const onOrphanedConversationIdRef = useRef(onOrphanedConversationId);
  onOrphanedConversationIdRef.current = onOrphanedConversationId;
  const onWidgetDataRef = useRef(onWidgetData);
  onWidgetDataRef.current = onWidgetData;
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;
  // Distinguish LLM-driven navigation (Anaka's `navigate` action — KEEP the
  // voice call alive across it, the conversation continues on the new page) from
  // user-initiated navigation (CLOSE the call so a billable room + open mic
  // don't linger). Set when `onNavigate` fires; consumed by the route-change
  // effect below. A self-heal timer drops a stale grant if the navigate resolves
  // to no route change, so it can't suppress a later user navigation.
  const llmNavPendingRef = useRef(false);
  const llmNavClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const markFirstBlock = useCallback(() => {
    // Spec 014 US1: once Anaka's first content block arrives the cold-entry
    // pills have served their purpose — clear them so they don't linger
    // alongside the assistant's first turn.
    setEntryActions((prev) => (prev.length > 0 ? [] : prev));
    if (firstContentBlockReceivedRef.current) return;
    firstContentBlockReceivedRef.current = true;
    setFirstContentBlockReceived(true);
  }, []);

  const handleTextContentBlock = useCallback((block: ContentBlock) => {
    markFirstBlock();
    // 004-p6 (T016) — SSE widget blocks go through the id-keyed replace path so a
    // re-stamped materialized card re-anchors in place; all other blocks append.
    if (block.type === 'widget') {
      appendOrReplaceWidgetBlock(block);
    } else {
      appendBlockToAssistantTurn(block);
    }
    // Real content supersedes any mid-turn status bridge line (documented
    // contract on `TextConversationOptions.onStatusUpdate`).
    setTextStatusUpdate(null);
    onContentBlockRef.current?.(block);
  }, [markFirstBlock, appendBlockToAssistantTurn, appendOrReplaceWidgetBlock]);

  const handleTextStatusUpdate = useCallback((data: { text: string; label: string }) => {
    setTextStatusUpdate(data);
  }, []);

  const handleVoiceWidgetData = useCallback(
    (payload: { event: string; widget_id?: string; data: Record<string, unknown> }) => {
      // 004-p6 (T014, R1) — LIFT the relayed `widget_id` when present so voice gets
      // id-parity with text and the id-keyed replace can fire. The gateway relay
      // shape becomes `{ event, widget_id, data }` with the id as a SIBLING (T013,
      // server-side, gated); tolerate today's shape where a materialized card's id
      // may instead ride inside `data.widget_id`. Only mint the synthetic
      // `voice_…` id as a fallback for genuinely id-less frames (the old
      // unconditional overwrite was the bug that kept voice replace from ever
      // firing — useConversation.ts contract, card-events.md §Voice).
      const dataWidgetId = payload.data?.widget_id;
      const relayedWidgetId =
        (typeof payload.widget_id === 'string' && payload.widget_id.length > 0 && payload.widget_id) ||
        (typeof dataWidgetId === 'string' && dataWidgetId.length > 0 && dataWidgetId) ||
        null;
      const block: WidgetBlock = {
        type: 'widget',
        widget_type: payload.event,
        widget_id: relayedWidgetId ?? `voice_${Date.now()}_${++voiceWidgetCounterRef.current}`,
        data: payload.data,
      };
      markFirstBlock();
      appendOrReplaceWidgetBlock(block);
      onContentBlockRef.current?.(block);
      onWidgetDataRef.current?.(payload);
    },
    [markFirstBlock, appendOrReplaceWidgetBlock],
  );

  // Fix B (spec 032) — a spoken-transcript turn the agent published over the data
  // channel. Route it into `turns` through the SAME path text uses, so voice
  // turns appear live: a user turn mirrors text's optimistic `appendUserTurn`; an
  // assistant turn mirrors a streamed text block (`handleTextContentBlock`) —
  // mark-first-content + append/coalesce + notify. This is why voice no longer
  // needs a transcript poll: the data channel is its live source, like SSE is for
  // text.
  const handleVoiceTranscript = useCallback(
    (data: VoiceTranscriptData) => {
      // Reserved for future interim STT streaming: ignore non-final fragments so a
      // partial transcript can't churn the thread. The agent MVP only emits final
      // turns (`final` omitted ⇒ final).
      if (data.final === false) return;
      if (data.role === 'user') {
        appendUserTurn(data.text);
        return;
      }
      const block: ContentBlock = { type: 'text', text: data.text };
      markFirstBlock();
      appendBlockToAssistantTurn(block);
      onContentBlockRef.current?.(block);
    },
    [appendUserTurn, markFirstBlock, appendBlockToAssistantTurn],
  );

  const text = useTextConversation({
    projectId,
    mode,
    conversationId: lifecycleConversationId,
    // Share the single lazy-start id with voice (which keys its LiveKit room on
    // the same `fallbackConversationId`). On the home "type-and-go" path a
    // voice-first session that later sends text then resolves to ONE
    // conversation_id instead of minting a divergent UUID. See
    // 60-Backlog/voice-room-id-divergence.md.
    lazyConversationId: fallbackConversationId,
    transportSurfaceKey: surfaceKey,
    activityId,
    participantId,
    currentPage,
    lastSeenTurnId: turns[turns.length - 1]?.turn_id,
    onContentBlock: handleTextContentBlock,
    onStatusUpdate: handleTextStatusUpdate,
    onTurnActions: handleTurnActions,
    onCloudIdleEnded: (d) => onCloudIdleEndedRef.current?.(d),
    onCloudGuardrailEnded: (d) => onCloudGuardrailEndedRef.current?.(d),
    onReconnectNotice: (d) => onReconnectNoticeRef.current?.(d),
    onNavigate: (e) => {
      // This route change is Anaka's (the LLM's) — mark it so the route-change
      // effect keeps the voice call alive. Self-heal the grant after a short
      // window so a navigate that resolves to no actual route change can't
      // suppress the next user navigation.
      llmNavPendingRef.current = true;
      if (llmNavClearRef.current) clearTimeout(llmNavClearRef.current);
      llmNavClearRef.current = setTimeout(() => { llmNavPendingRef.current = false; }, 2_000);
      onNavigateRef.current?.(e);
    },
    onError,
  });

  // The id this session resolves to BEFORE the server returns one this render.
  // Mirrors the text reconnect/hydrate precedence (see startSession + the
  // hydrate effect): the briefing-context resume id, else the lifecycle
  // (sessionStorage) id. An explicit `resumeConversationId === null` means
  // briefing-context found NO resumable session, so it must NOT fall back to a
  // possibly-stale lifecycle id. Shared by both transports so voice and text
  // never fork onto different conversations (spec 032 "one id across text+voice").
  const reconnectConversationId =
    resumeConversationId === undefined
      ? (lifecycleConversationId ?? null)
      : (resumeConversationId ?? null);

  // FR-001a (spec 032) — FREEZE the session conversation identity. The voice room
  // + the exposed id must key on ONE stable value for the session, or a change in
  // the derived precedence below (as `text.conversationId` / `reconnectConversationId`
  // resolve, or a briefing-context refetch flips `resumeConversationId`) RE-KEYS a
  // live LiveKit room → repeated CLIENT_INITIATED reconnects (the scoping-resume
  // "reconnect storm" → audio silence). Latch the first AUTHORITATIVE id and reuse
  // it for the session (the surface-reset effect clears the ref + re-mints the
  // fallback for a genuinely new session):
  //   • the server-resolved `text.conversationId` if present (most authoritative);
  //   • else, once resume state has SETTLED (`resumeConversationId !== undefined`)
  //     and no /start is in flight — mirroring the connectVoice deferral, since we
  //     must not commit to a client id while the server may still resolve a
  //     different one (#881) — the resume/lifecycle/lazy id.
  // Until it latches, the raw candidate is used transiently; voice connect is
  // deferred in that window (connectVoice), so no live room is re-keyed.
  const candidateConversationId =
    // 032 Phase D (US2 / MF7) — a born-canonical adoption is the MOST authoritative
    // id: it is the id the LiveKit room was actually minted on, so it outranks even
    // the server-resolved text id. (It also re-seats the frozen ref in the adopter
    // callback, so this is belt-and-braces for the render before the ref is read.)
    adoptedVoiceConversationId ?? text.conversationId ?? reconnectConversationId ?? fallbackConversationId;
  // Latch on the FIRST server-confirmed id. The server uses the client id
  // verbatim on a 032 reconnect, so this is the resume id on a resume and the
  // lazy id on a cold start. Waiting for the server — rather than committing to a
  // client resume id up front — preserves the #881 deferral: a /start may resolve
  // a different id than a stale client `reconnectConversationId`, and voice must
  // key on the server's. Before it latches, the raw `candidateConversationId` is
  // used, but `connectVoice` defers the LiveKit connect while a /start is in
  // flight, so no live room is keyed on the transient value. Once latched it is
  // FROZEN — a later change to `text.conversationId` / `reconnectConversationId`
  // (e.g. a briefing-context refetch flip) can no longer re-key the live room.
  if (sessionConversationIdRef.current == null && text.conversationId) {
    sessionConversationIdRef.current = text.conversationId;
  }
  const sessionConversationId = sessionConversationIdRef.current ?? candidateConversationId;

  // FR-001a invariant guard (P2 review). The frozen session id assumes the
  // server echoes the client id verbatim on a 032 reconnect — so a latched id
  // should only ever see `text.conversationId` clear-and-return the SAME value,
  // never switch to a DIFFERENT server id without a surface reset. We do NOT
  // re-key on such a change (re-keying a live room is the storm; a genuine new
  // session resets via `surfaceKey`), but if the invariant ever breaks the voice
  // room + exposed id would silently pin to the stale conversation while text
  // operates on the new one. Warn loudly (fires only on an actual id change, not
  // per render) so a future contract drift fails visibly instead of reading the
  // wrong conversation.
  useEffect(() => {
    const frozen = sessionConversationIdRef.current;
    if (frozen != null && text.conversationId != null && text.conversationId !== frozen) {
      console.warn(
        '[useConversation] FR-001a: frozen session id diverged from a new server id — not re-keying (a new session must reset the surface)',
        { frozen, serverId: text.conversationId },
      );
    }
  }, [text.conversationId]);

  const voiceHook = useVoiceConversation({
    projectId,
    mode,
    // Resolve voice's id with the same precedence the text path uses on
    // reconnect: server-resolved id › resume/lifecycle reconnect id › client
    // lazy fallback. Without `reconnectConversationId` here, switching to voice
    // on a resumed scoping session (where the real id arrives via
    // briefing-context and `text.conversationId` is still null) connected
    // LiveKit under a fresh fallback UUID — a brand-new empty conversation that
    // wiped the transcript + rail (voice↔text id divergence on resume). Now the
    // FROZEN `sessionConversationId` (FR-001a) so a live room is never re-keyed.
    conversationId: sessionConversationId,
    activityId,
    participantId,
    onWidgetData: handleVoiceWidgetData,
    onTranscript: handleVoiceTranscript,
    // 032 Phase D (US2 / ANA-612 / MF7) — the server minted the voice room on a
    // fresh id (owned-but-expired resume). Adopt it as the authoritative session
    // id: re-seat the frozen ref (so a subsequent reconnect/hydrate targets the
    // real room, not the dead one) and force a re-render via state so the exposed
    // `conversationId` + the id passed back into voice both track it. Fires BEFORE
    // room.connect, so re-seating the frozen id here does NOT re-key a live room.
    onConversationIdResolved: (resolvedId: string) => {
      sessionConversationIdRef.current = resolvedId;
      setAdoptedVoiceConversationId(resolvedId);
    },
    // ANA-575 — the agent intentionally ended the voice session. `error` never
    // reaches here (the hook keeps its retryable error path), so any reason we
    // get is a graceful close the parent turns into a lifecycle end.
    onVoiceSessionEnded: (reason) => {
      if (reason === 'error') return;
      onVoiceSessionEndedRef.current?.(reason);
    },
    onError,
  });

  // Voice session start timestamp. Set when `connectVoice()` resolves so the
  // session timer has a stable anchor; cleared on `disconnectVoice()`.
  const [voiceStartedAt, setVoiceStartedAt] = useState<number | null>(null);

  const voiceConnect = voiceHook.connect;
  const voiceDisconnect = voiceHook.disconnect;

  const runVoiceConnect = useCallback(async () => {
    // `useVoiceConversation.connect()` swallows transport errors (it sets
    // its own `voiceState='error'` and resolves), so a successful await does
    // NOT imply a working session. Branch on the explicit `{ ok }` flag
    // instead of `voiceHook.isConnected` — the captured `voiceHook` snapshot
    // would still report the *pre*-connect value (false on a cold start) and
    // a stale-closure read would gate out the timer anchor on every first
    // connect.
    // Bug B (ANA-614) — the agent runs `session.start` (a greeting/recap) on
    // EVERY fresh room join, so a resume gets a spoken recap too. The old
    // `turns === 0` gate mislabelled a resume as 'listening' (no greeting) even
    // though a recap was coming. Now: always show the cycle, and mark whether a
    // missing greeting is a hard error (cold start, turns===0) or a soft degrade
    // to 'listening' (resume, turns>0 — the recap is a full LLM turn and may be
    // slow). Flag OFF restores the exact prior `turns===0` behaviour.
    const isResume = turnsLengthRef.current > 0;
    const { ok } = await voiceConnect(
      VOICE_RESUME_GREETING_CYCLE_ENABLED
        ? { expectGreeting: true, greetingTimeoutIsHardError: !isResume }
        : { expectGreeting: turnsLengthRef.current === 0 },
    );
    if (ok) {
      setVoiceStartedAt(Date.now());
    }
  }, [voiceConnect]);

  const connectVoice = useCallback(async () => {
    // Voice-first id convergence. When a text session is being established
    // (proactiveStart project pages, or a ChatThread-driven start) the SERVER
    // owns the conversation_id and may RESUME or MINT an id the client cannot
    // predict. Observed on a scoping resume: the client's lifecycle/resume id
    // was `40c48ce5…` but `/start` resolved a *different* `5a388d59…`, so a voice
    // room keyed on the client id diverged from the text conversation — voice
    // and text ended up on separate ids (the agent's /turn writes one, the
    // transcript/rail read the other).
    //
    // So whenever a start is in flight, DEFER the LiveKit connect until the
    // server-resolved `text.conversationId` arrives, then key the room on THAT
    // (the deferred-connect effect below re-fires once it resolves). We no
    // longer treat a client-side `reconnectConversationId` as authoritative for
    // the room id — it can be stale, which is the divergence above. The pure
    // lazy path (home) never calls startSession, so the shared client id IS
    // authoritative and we connect immediately. See
    // 60-Backlog/voice-room-id-divergence.md.
    if (startSessionAttemptedRef.current && !text.conversationId) {
      pendingVoiceConnectRef.current = true;
      return;
    }
    await runVoiceConnect();
  }, [text.conversationId, runVoiceConnect]);

  // Fire a deferred voice connect once the server-resolved conversation_id
  // arrives. Keyed on `text.conversationId` so it runs exactly when the id
  // transitions null → resolved; the voice hook has by then re-rendered with
  // that id, so the LiveKit room keys on the server id.
  useEffect(() => {
    if (!pendingVoiceConnectRef.current) return;
    if (!text.conversationId) return;
    pendingVoiceConnectRef.current = false;
    void runVoiceConnect();
    // ANA-162: clear flag on unmount so a late-resolving id doesn't trigger a
    // room connection after the user has navigated away.
    return () => { pendingVoiceConnectRef.current = false; };
  }, [text.conversationId, runVoiceConnect]);

  // Voice on navigation. The conversation provider is an App-root singleton that
  // never unmounts on a route change, so the voice hook's own unmount teardown
  // never fires — without this, a billable LiveKit room + open mic would keep
  // running (and the tab keeps recording) after the user moves on.
  //   • LLM-driven navigation (Anaka's `navigate` action, 020/#627) KEEPS the
  //     call — the conversation continues on the new page (the one-shot grant
  //     set in the onNavigate wrapper above).
  //   • USER-initiated navigation CLOSES the call — raw `voiceDisconnect` (NOT
  //     `disconnectVoice`, so the text lifecycle isn't reset and the
  //     conversation stays warm). No-op when no call is active.
  // `currentPage` is the route the provider passes down; it changes exactly on
  // navigation, so within-page layout toggles (peek↔detailed↔focused) keep the
  // call. (Voice-driven LLM `navigate` is not wired yet — ANA follow-up — so for
  // a voice call today this closes on all navigation, which is the desired
  // close-on-user-nav; the gate is forward-compatible.)
  const prevPageRef = useRef(currentPage);
  useEffect(() => {
    if (prevPageRef.current === currentPage) return;
    prevPageRef.current = currentPage;
    if (llmNavPendingRef.current) {
      llmNavPendingRef.current = false; // consume the one-shot LLM-nav grant; keep the call
      if (llmNavClearRef.current) { clearTimeout(llmNavClearRef.current); llmNavClearRef.current = null; }
      return;
    }
    // USER navigation closes the call so a billable LiveKit room + open mic don't
    // survive — or get opened AFTER — the navigation. Three windows to cover:
    //  • a live room (`voiceHook.room`);
    //  • a DEFERRED connect (`pendingVoiceConnectRef`) still waiting on
    //    text.conversationId — without cancelling it, the deferred-connect effect
    //    would still open a room once the id resolves, after the user has left;
    //  • an IN-FLIGHT connect (voiceState authorizing/connecting/preparing) where
    //    the hook's roomRef can exist before `voiceHook.room` reflects it —
    //    voiceDisconnect() defers teardown until the handshake settles.
    const isConnectingVoice =
      voiceHook.voiceState === 'authorizing' ||
      voiceHook.voiceState === 'connecting' ||
      voiceHook.voiceState === 'preparing';
    if (voiceHook.room || pendingVoiceConnectRef.current || isConnectingVoice) {
      pendingVoiceConnectRef.current = false;
      voiceDisconnect();
      setVoiceStartedAt(null);
    }
  }, [currentPage, voiceHook.room, voiceHook.voiceState, voiceDisconnect]);

  // Ref so `disconnectVoice` can call the latest callback without re-creating
  // (and so it stays out of the callback's dep array).
  const onEndConversationRef = useRef(onEndConversation);
  onEndConversationRef.current = onEndConversation;

  const disconnectVoice = useCallback(() => {
    pendingVoiceConnectRef.current = false;
    voiceDisconnect();
    setVoiceStartedAt(null);
    // 015 — FR-VOICE-003: End closes the room AND ends the conversation, so the
    // next Start is fresh (not a reconnect). Pause uses `pauseVoice`, which does
    // not reset. Mode-switch to text uses the raw `voiceDisconnect`, not this.
    onEndConversationRef.current?.();
  }, [voiceDisconnect]);

  const setInputMode = useCallback((nextMode: InputMode, opts?: { autoStartText?: boolean }) => {
    // Default true: switching to text after a stalled first turn kicks a fresh text
    // start so the composer unblocks (ANA-282). Owners that start the session
    // themselves pass false to avoid a double-start (ANA-381). See the interface doc.
    const autoStartText = opts?.autoStartText ?? true;
    if (nextMode === 'text') {
      // Cancel any voice connect that was deferred awaiting the conversation_id
      // so it can't fire late after the user has switched to text.
      pendingVoiceConnectRef.current = false;
      if (voiceHook.isConnected) {
        voiceDisconnect();
        setVoiceStartedAt(null);
      }
      // Clear any terminal voice error so the stale "Something went wrong"
      // banner doesn't linger after switching back to text. A failed connect
      // is not "connected", so the disconnect branch above never fires for it.
      voiceHook.clearError();
      // ANA-282: voice was attempted but produced no content (failed connect /
      // disconnected before the first turn). The proactiveStart guard is already
      // tripped (startSessionAttemptedRef.current === true), so the effect won't
      // re-fire. Manually kick off a text session so the composer unblocks,
      // preserving the same reconnect args the startSession wrapper would compute.
      // ANA-381: skipped when autoStartText === false — the caller owns the start,
      // so kicking here too would double-start (e.g. beginCall's startSession).
      if (autoStartText && startSessionAttemptedRef.current && !firstContentBlockReceivedRef.current) {
        const prev = reconnectConversationId;
        const startArgs = prev != null && prev !== ''
          ? { isReconnect: true, previousConversationId: prev }
          : undefined;
        void text.startSession(startArgs);
      }
    }
    setInputModeState(nextMode);
  }, [voiceHook.isConnected, voiceDisconnect, voiceHook.clearError, text.startSession, reconnectConversationId]);

  // Pull the voice hook's accumulated pause time on each send so it rides
  // along on the very next message. The voice hook owns the counter (Slice 5
  // boundary); we just consume it here at the message-send splice point. We
  // also append the user turn locally before delegating so `<ChatThread>`
  // sees the message in `turns` immediately, even if the network is slow.
  const sendMessage = useCallback(async (msg: string, sendOpts?: Omit<SendMessageOptions, 'elapsedPauseMs'>) => {
    // Spec 014 US1: the user acting (typing, or clicking an entry pill which
    // routes through here) consumes the cold-entry pills — clear them so they
    // don't persist once the conversation is under way.
    setEntryActions((prev) => (prev.length > 0 ? [] : prev));
    appendUserTurn(msg);
    const elapsedPauseMs = voiceHook.consumeElapsedPause();
    // M1 — the user turn is appended optimistically above. `text.sendMessage`
    // now rejects on genuine send/stream failure (see useTextConversation), so
    // catch here to avoid an unhandled rejection in UI submit handlers. The
    // failure is already surfaced through `text.error` (exposed as `error`) and
    // is retryable via `retry()`, which re-sends the same text without
    // appending a second user turn — so the optimistic bubble stays visible and
    // is never silently duplicated.
    try {
      await text.sendMessage(msg, {
        ...sendOpts,
        ...(elapsedPauseMs > 0 ? { elapsedPauseMs } : {}),
      });
    } catch {
      /* error surfaced via text.error; retry() re-sends without re-appending */
    }
  }, [text.sendMessage, voiceHook.consumeElapsedPause, appendUserTurn]);

  const startSession = useCallback(async (opts?: { deferOpening?: boolean }) => {
    // Don't fire before the project resolves — the proactiveStart effect (which
    // already guards on !projectId) will retry once projectId arrives.  Without
    // this, ChatThread's proactiveStart effect claims the attempt slot while
    // projectId is still '' and the subsequent surfaceKey change aborts the
    // in-flight POST /start (ANA-190).
    if (!projectId) return;
    startSessionAttemptedRef.current = true;
    setStartSessionAttempted(true);

    // The reconnect target (briefing-context resume id › lifecycle id, with an
    // explicit null overriding a stale lifecycle id) — the same id voice keys on.
    const prev = reconnectConversationId;
    const reconnectArgs =
      prev != null && prev !== ''
        ? { isReconnect: true as const, previousConversationId: prev }
        : undefined;

    // v1.0.4 — voice opening de-dupe. The caller (beginCall voice path) sets
    // `deferOpening` so this start only RESOLVES the server conversation_id; the
    // LiveKit agent's session.start then owns the single spoken opening. Without
    // it both starts greet (text bubble + voice).
    const startArgs =
      opts?.deferOpening
        ? { ...(reconnectArgs ?? {}), deferOpening: true as const }
        : reconnectArgs;

    await text.startSession(startArgs);
  }, [text.startSession, reconnectConversationId, projectId]);

  // proactiveStart: kick off startSession once projectId resolves. Guarded by
  // the ref so React 18 strict-mode double-invocation doesn't fire twice.
  // Home lobby uses a lazy-start UX (see useTextConversation.sendMessage):
  // first send mints `conversation_id` client-side and skips /start.
  const startSessionRef = useRef(startSession);
  startSessionRef.current = startSession;
  useEffect(() => {
    if (!proactiveStart) return;
    if (!projectId) return;
    if (startSessionAttemptedRef.current) return;
    void startSessionRef.current();
  }, [proactiveStart, projectId, surfaceKey]);

  // T022/T023 — silent transcript hydrate (020 US4 D3). Fires when the surface
  // is a detailed conversation view AND there is a prior conversation id
  // AND no warm_continue (proactiveStart takes precedence). Uses an effect-local
  // request token ref (D4) to drop stale responses when conversationId rotates.
  // resumeConversationId provides the id for cross-session scoping resume.
  // Same semantics as startSession: null = explicit no-hydrate (override stale sessionStorage).
  const hydrateTokenRef = useRef<string | null>(null);
  // Ids that already 404'd on hydrate. `onOrphanedConversationId` (→ lifecycle.reset)
  // rotates lifecycle/surface deps, which re-runs this effect; without this guard a
  // persistent dead id (e.g. a briefing-context id deleted mid-flight) would refetch →
  // 404 → reset → refetch forever. Once an id 404s we never fetch it again.
  const hydrated404Ref = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (proactiveStart) return; // warm_continue takes precedence
    if (!isDetailedConversationSurface) return;
    // Expired (deferred) resume: the user re-enters explicitly via the orient card,
    // so don't auto-hydrate the prior transcript until the voice/text click has
    // fired startSession — otherwise hydrated turns would skip the orient card.
    if (resumeIsDeferred && !startSessionAttempted) return;
    const hydrateId = resumeConversationId === undefined
      ? (lifecycleConversationId ?? null)
      : resumeConversationId;
    if (!hydrateId) return;
    if (hydrated404Ref.current.has(hydrateId)) return; // already known-dead — don't refetch (no loop)

    const token = hydrateId;
    hydrateTokenRef.current = token;
    let cancelled = false;
    // ANA-371 — mark the hydrate in flight so the surface can render a message
    // skeleton until history lands (or the fetch settles empty / errored).
    setIsHydratingTranscript(true);

    const run = async () => {
      try {
        const res = await fetch(`/api/v1/conversation/${token}/turns`, {
          credentials: 'include',
        });
        if (cancelled) return;
        if (res.status === 404) {
          // The id has no server row (expired / self-heal-swept / rolled back).
          // Record it so this effect never re-fetches it (the reset below rotates
          // deps and re-runs the effect), then clear it so the next boot starts fresh
          // rather than reconnecting a dead conversation (P1.3).
          hydrated404Ref.current.add(token);
          onOrphanedConversationIdRef.current?.();
          return;
        }
        if (!res.ok) return;
        const json = (await res.json()) as { turns?: unknown[] };
        if (cancelled) return;
        // D4: stale overwrite guard — if conversationId changed while in-flight, drop
        if (hydrateTokenRef.current !== token) return;
        const hydrated = mapServerTurnsToV2(json.turns ?? []);
        if (hydrated.length > 0) {
          // Merge rather than blind-replace: a deferred (expired) resume fires
          // hydration and the reconnect turn from the same click, so a "welcome
          // back" turn may already have been appended. Keep any such live turns
          // after the restored history; drop server dupes by turn_id. For the
          // common case (prev empty) this is exactly setTurns(hydrated).
          setTurns((prev) => {
            // 004-p6 (T018) — final-state assembly on the rehydrated transcript:
            // materialized cards rendered ONCE at their final position with
            // `card_moved` markers at prior positions, and legacy model-authored
            // Cloud-owned card blocks dropped (double-render fix, R10).
            const assembled = assembleFinalStateTurns(hydrated);
            if (prev.length === 0) return assembled;
            const restored = new Set(assembled.map((t) => t.turn_id));
            const merged = [...assembled, ...prev.filter((t) => !restored.has(t.turn_id))];
            // 004-p6 (T016, F3.2) — the turn_id dedupe above can't catch a
            // materialized singleton card that appears in BOTH the rehydrated
            // final state and a live in-session turn (different turn_ids, same
            // stable widget_id) → both render. Collapse by widget_id too, keeping
            // the frontier-most (live) copy.
            return dedupeCardsByWidgetId(merged);
          });
        }
      } catch {
        // Hydrate errors are silent — don't surface to user
      } finally {
        // Clear the in-flight flag once this fetch settles (success / empty / 404 /
        // error). When `cancelled`, the effect cleanup owns the reset instead.
        if (!cancelled) setIsHydratingTranscript(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
      setIsHydratingTranscript(false);
    };
  }, [proactiveStart, isDetailedConversationSurface, resumeIsDeferred, startSessionAttempted, lifecycleConversationId, resumeConversationId, surfaceKey]);

  const error = text.error ?? voiceHook.error;

  const phase = useMemo<ConversationPhase>(() => {
    if (error != null) return 'error';
    // Lazy-start (home) sets `text.conversationId` from /message's `session`
    // SSE event without ever calling /start, so treat a live conversation_id
    // as "past mounting" even when startSession was never invoked.
    if (!startSessionAttempted && !text.conversationId) {
      if (!projectId && mode !== 'home') return 'mounting';
      if (projectId) return 'mounting';
    }
    if (!text.conversationId) return 'connecting';
    if (!firstContentBlockReceived) return 'awaiting_first_turn';
    return 'ready';
  }, [
    error,
    projectId,
    mode,
    startSessionAttempted,
    text.conversationId,
    firstContentBlockReceived,
  ]);

  // 004-p7 A3.2 (ANA-658) — is it SAFE to start a voice call right now? A surface
  // that auto-starts text (scoping/proactiveStart) must not connect voice before the
  // server resolves the conversation id: `connectVoice` would key the LiveKit room on a
  // fresh `fallbackConversationId` that then diverges from the server id (voice↔text
  // split-brain — see the connectVoice deferral at the top of this hook). Rather than
  // silently defer the connect, the UI DISABLES the voice-start control until this is
  // true. Ready ⇔ the server id is committed, OR this surface will NOT auto-start text
  // (pure-lazy home: the shared client id is already authoritative, so voice is safe
  // immediately). Mirrors the connectVoice deferral predicate (`startSessionAttemptedRef
  // && !text.conversationId`) plus the pre-fire proactiveStart window. Never latches
  // permanently-false: a text-starting surface resolves `text.conversationId` on success
  // and hits `phase === 'error'` on a failed `/start` (pr-review P2/QA — otherwise the
  // control dead-locks "Getting ready…"). Rule extracted to a pure, unit-tested helper.
  const voiceStartReady = computeVoiceStartReady({
    conversationId: text.conversationId ?? null,
    phase,
    startSessionAttempted,
    proactiveStart,
  });

  const pauseVoice = voiceHook.pause;
  const resumeVoice = voiceHook.resume;
  const toggleMute = voiceHook.toggleMute;
  const extendSession = voiceHook.extendSession;
  const requestLockScope = voiceHook.requestLockScope;

  return useMemo(
    (): ConversationReturn => ({
      inputMode,
      setInputMode,
      // The FROZEN session id (FR-001a) — the same stable value the voice hook
      // keys its LiveKit room on, so every id consumer (rail, hydrate, /turns)
      // reads one identity for the session (not a stray fallback UUID or a
      // mid-session re-key on a resumed session).
      conversationId: sessionConversationId,
      isThinking: text.isThinking,
      isResponding: text.isResponding,
      voiceState: voiceHook.voiceState,
      isConnected: voiceHook.isConnected,
      isMuted: voiceHook.isMuted,
      paused: voiceHook.paused,
      pauseReason: voiceHook.pauseReason,
      pausedMs: voiceHook.elapsedPauseMs,
      voiceStartedAt,
      error,
      room: voiceHook.room,
      livekitUrl: voiceHook.livekitUrl,
      micBlocked: voiceHook.micBlocked,
      micErrorKind: voiceHook.micErrorKind,
      // ANA-728/ANA-738 (B1) — the mic is auto-locked during `thinking`; the bar
      // shows the honest "Anaka is working — tap to interrupt" cue.
      micLocked: voiceHook.micLocked,
      statusLabelOverride: voiceHook.statusLabelOverride,
      voiceEndedReason: voiceHook.endedReason,
      textStatusUpdate,
      phase,
      isReady: phase === 'ready',
      inputReady:
        phase === 'awaiting_first_turn' || phase === 'ready',
      // A3.2 — voice-start gate (see the derivation above). The UI disables the
      // voice-start control while false so a scoping/proactive surface can't key a
      // LiveKit room on a fallback id before the server id resolves.
      voiceStartReady,
      turns,
      isHydratingTranscript,
      entryActions,
      startSession,
      sendMessage,
      retry: text.retry,
      connectVoice,
      disconnectVoice,
      pauseVoice,
      resumeVoice,
      toggleMute,
      idlePrompting: voiceHook.idlePrompting,
      sessionCapWarning: voiceHook.sessionCapWarning,
      extendSession,
      lockScopeRequested: voiceHook.lockScopeRequested,
      requestLockScope,
    }),
    [
      inputMode,
      setInputMode,
      text.conversationId,
      reconnectConversationId,
      fallbackConversationId,
      text.isThinking,
      text.isResponding,
      text.retry,
      voiceHook.voiceState,
      voiceHook.isConnected,
      voiceHook.isMuted,
      voiceHook.paused,
      voiceHook.elapsedPauseMs,
      voiceHook.room,
      voiceHook.livekitUrl,
      voiceHook.micBlocked,
      voiceHook.micErrorKind,
      voiceHook.micLocked,
      voiceHook.statusLabelOverride,
      voiceHook.idlePrompting,
      voiceHook.sessionCapWarning,
      extendSession,
      voiceHook.lockScopeRequested,
      requestLockScope,
      voiceStartedAt,
      error,
      textStatusUpdate,
      phase,
      voiceStartReady,
      turns,
      isHydratingTranscript,
      entryActions,
      startSession,
      sendMessage,
      connectVoice,
      disconnectVoice,
      pauseVoice,
      resumeVoice,
      toggleMute,
    ],
  );
}
